import React, { useMemo } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { NodeViewWrapper, ReactNodeViewProps } from '@tiptap/react';

export interface InkStroke {
  /** Flat polyline: [x1, y1, x2, y2, ...] in the block's own coordinate space. */
  points: number[];
}

const CONFIDENCE_THRESHOLD = 0.6; // mirrors gn.ink.confidence-threshold's default (backend is authoritative)

/**
 * Renders captured pen strokes as SVG polylines (spec 046 FR-002/FR-022). The ink is
 * always the student's own handwriting, never restyled or replaced by its interpretation —
 * `interpretedText` lives only in node attrs for the AI features to read (Decision 4).
 * When interpretation confidence is low, a visible marker signals GN couldn't confidently
 * read it (FR-021 — ask, don't guess) rather than silently acting on a bad transcription.
 * Stroke *capture* is handled by InkLayer (US6); this view is display + persistence.
 */
const InkBlockNode: React.FC<ReactNodeViewProps> = ({ node }) => {
  const { strokes, width, height, confidence } = node.attrs as {
    strokes: string; width: number; height: number; confidence?: number;
  };

  const parsedStrokes: InkStroke[] = useMemo(() => {
    try {
      const parsed = JSON.parse(strokes || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [strokes]);

  const lowConfidence = confidence != null && confidence > 0 && confidence < CONFIDENCE_THRESHOLD;

  return (
    <NodeViewWrapper as="div">
      <Box
        sx={{ my: 1, borderRadius: 1, border: '1px dashed', borderColor: 'divider', overflow: 'hidden', position: 'relative' }}
        data-testid="gn-ink-block"
      >
        {lowConfidence && (
          <Tooltip title="I couldn't confidently read this handwriting — AI features may ask you about it instead of guessing.">
            <Chip
              icon={<HelpOutlineIcon sx={{ fontSize: '14px !important' }} />}
              label="Not sure I read this right"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ position: 'absolute', top: 4, right: 4, height: 20, fontSize: '0.65rem' }}
              data-testid="gn-ink-low-confidence"
            />
          </Tooltip>
        )}
        <svg
          viewBox={`0 0 ${width || 600} ${height || 200}`}
          style={{ display: 'block', width: '100%', maxWidth: width || 600, touchAction: 'none' }}
        >
          {parsedStrokes.map((stroke, i) => (
            <polyline
              key={i}
              points={(stroke.points || []).join(' ')}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>
      </Box>
    </NodeViewWrapper>
  );
};

export default InkBlockNode;
